"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { type Locale } from "@/i18n/config";
import { fetchActiveLocales } from "@/lib/active-locales";
import { PortaledMenu } from "@/components/ui";
import { updateProfile } from "@/lib/api";
import { getMe } from "@/lib/auth-store";
import { cn } from "@/lib/cn";
import { localeCookieString } from "@/lib/locale-preference";

const LOCALE_LABEL_KEY: Record<
  Locale,
  "localeEn" | "localeDe" | "localeFr" | "localeEs" | "localeIt" | "localeNl" | "localeRu" | "localeTr"
> = {
  en: "localeEn",
  de: "localeDe",
  fr: "localeFr",
  es: "localeEs",
  it: "localeIt",
  nl: "localeNl",
  ru: "localeRu",
  tr: "localeTr",
};

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13.4 13.4 0 0 1 3.5 9 13.4 13.4 0 0 1-3.5 9 13.4 13.4 0 0 1-3.5-9A13.4 13.4 0 0 1 12 3Z" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * Language picker: a compact globe trigger that opens a dropdown of the
 * active locales (native names). Switches the URL locale in place and, for
 * signed-in members, persists the choice so emails follow it. The menu is
 * portaled, so it works in the nav rail, the landing footer and any future
 * placement without overflow clipping.
 */
export function LocaleSwitcher({ className }: { className?: string }) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations("common");
  const [active, setActive] = useState<Locale[] | null>(null);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let mounted = true;
    fetchActiveLocales().then((locales) => {
      if (mounted) setActive(locales);
    });
    return () => {
      mounted = false;
    };
  }, []);

  function switchTo(next: Locale) {
    setOpen(false);
    if (next === locale) return;
    // Explicit choice: persist it so the proxy routes future bare-path visits
    // (and the suggestion banner stays quiet). 'en' is a no-op for the proxy
    // but still silences the banner.
    // eslint-disable-next-line react-hooks/immutability
    document.cookie = localeCookieString(next, window.location.protocol === "https:");
    // Read the query at click time; useSearchParams would force a Suspense
    // boundary into every static page that renders the switcher.
    const query = window.location.search;
    router.replace(`${pathname}${query}`, { locale: next });
    if (getMe()) {
      // Fire-and-forget; a failed persist must not block the UI switch.
      updateProfile({ locale: next }).catch(() => {});
    }
  }

  // Until the active set is known, render nothing; with a single active
  // locale there is nothing to switch to.
  if (!active || active.length < 2) return null;

  return (
    <div className={className}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("language")}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5",
          "text-xs font-semibold uppercase tracking-wide transition-colors",
          open ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white",
        )}
      >
        <GlobeIcon className="h-3.5 w-3.5" />
        {locale}
        <ChevronIcon className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>

      <PortaledMenu
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        width={176}
        className="glass-panel overflow-hidden rounded-xl border border-white/10 bg-elevated/95 py-1 shadow-lift"
      >
        {active.map((code) => (
          <button
            key={code}
            type="button"
            role="menuitemradio"
            aria-checked={code === locale}
            lang={code}
            onClick={() => switchTo(code)}
            className={cn(
              "flex w-full items-center justify-between px-3.5 py-2 text-left text-sm transition-colors",
              code === locale
                ? "text-flamingo"
                : "text-white/75 hover:bg-white/5 hover:text-white",
            )}
          >
            {t(LOCALE_LABEL_KEY[code])}
            <span className="text-2xs font-semibold uppercase tracking-wide text-white/35">
              {code}
            </span>
          </button>
        ))}
      </PortaledMenu>
    </div>
  );
}
